import { useState, useEffect, useRef } from 'react';
import { SPLIT_CASE_CONDITION_FULL } from '_utils/constant';

export const DEFAULT_SPLIT_CASE_SETTINGS = {
  itemsPerCase: '',
  costPerItem: '',
  minimumItemOrder: '1',
  fulfilmentOption: 'full', // 'full' | 'incomplete'
  fulfillCondition: SPLIT_CASE_CONDITION_FULL,
  acknowledgePayLater: false,
};

export default function useSplitCasePurchase({
  values: externalValues,
  itemPrice,
  onChange,
  onReset,
} = {}) {
  const [internalValues, setInternalValues] = useState(DEFAULT_SPLIT_CASE_SETTINGS);
  const [isHidden, setIsHidden] = useState(false);

  const currentValues = externalValues || internalValues;

  const isInitialMount = useRef(true);
  const prevItemsPerCase = useRef(currentValues.itemsPerCase);
  const prevItemPrice = useRef(itemPrice);

  const handleFieldChange = (field, value) => {
    const updated =
      typeof field === 'object' && field !== null
        ? { ...currentValues, ...field }
        : { ...currentValues, [field]: value };
    setInternalValues(updated);
    if (onChange) {
      onChange(updated);
    }
  };

  const handleReset = () => {
    setInternalValues(DEFAULT_SPLIT_CASE_SETTINGS);
    if (onReset) {
      onReset(DEFAULT_SPLIT_CASE_SETTINGS);
    }
    if (onChange) {
      onChange(DEFAULT_SPLIT_CASE_SETTINGS);
    }
  };

  useEffect(() => {
    const itemsPerCaseNum = parseFloat(currentValues.itemsPerCase);
    const priceNum = parseFloat(itemPrice);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevItemsPerCase.current = currentValues.itemsPerCase;
      prevItemPrice.current = itemPrice;
      return;
    }

    const itemsPerCaseChanged = currentValues.itemsPerCase !== prevItemsPerCase.current;
    const itemPriceChanged = itemPrice !== prevItemPrice.current;

    prevItemsPerCase.current = currentValues.itemsPerCase;
    prevItemPrice.current = itemPrice;

    const isCostEmpty =
      currentValues.costPerItem === undefined ||
      currentValues.costPerItem === null ||
      currentValues.costPerItem === '';

    if (
      itemsPerCaseNum > 0 &&
      priceNum > 0 &&
      (itemsPerCaseChanged || itemPriceChanged || isCostEmpty)
    ) {
      const timer = setTimeout(() => {
        const calculatedCost = (priceNum / itemsPerCaseNum).toFixed(2);
        handleFieldChange('costPerItem', calculatedCost);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [currentValues.itemsPerCase, itemPrice]);

  const toggleHidden = () => {
    setIsHidden((prev) => !prev);
  };

  return {
    values: currentValues,
    setValues: setInternalValues,
    isHidden,
    setIsHidden,
    toggleHidden,
    handleFieldChange,
    handleReset,
  };
}
